import { useEffect, useRef } from 'react'
import { Animated, Modal, Pressable, View } from 'react-native'
import { Divider, Icon, Text } from 'react-native-paper'
import { MenuProps } from './menu'
import { useTheme } from '../../../hooks/theme.hook'

export function MenuDrawer({
  visible,
  onDismiss,
  sections,
}: Omit<MenuProps, 'anchor' | 'anchorPosition'>) {
  const theme = useTheme()
  const slideAnim = useRef(new Animated.Value(300)).current

  useEffect(() => {
    if (visible) {
      Animated.spring(slideAnim, {
        toValue: 0,
        useNativeDriver: true,
        damping: 80,
        stiffness: 90,
      }).start()
    } else {
      Animated.timing(slideAnim, {
        toValue: 300,
        duration: 200,
        useNativeDriver: true,
      }).start()
    }
  }, [visible, slideAnim])

  const menuStyles = {
    modalOverlay: {
      flex: 1,
      backgroundColor: 'rgba(0, 0, 0, 0.5)',
      justifyContent: 'flex-end' as const,
    },
    menuContainer: {
      backgroundColor: theme.colors.surface,
      borderTopLeftRadius: theme.roundness * 2,
      borderTopRightRadius: theme.roundness * 2,
      paddingBottom: theme.spacing(2),
    },
    menuItem: {
      flexDirection: 'row' as const,
      alignItems: 'center' as const,
      paddingHorizontal: theme.spacing(3),
      paddingVertical: theme.spacing(2),
      minHeight: 48,
    },
    menuItemText: {
      marginLeft: theme.spacing(2),
      fontSize: 16,
      color: theme.colors.onSurface,
    },
    menuSectionLabel: {
      paddingHorizontal: theme.spacing(3),
      paddingTop: theme.spacing(2),
      paddingBottom: theme.spacing(1),
      color: theme.colors.onSurfaceVariant,
    },
  }

  const handleItemPress = (onPress: () => void) => {
    onPress()
    onDismiss()
  }

  return (
    <Modal
      visible={visible}
      transparent
      animationType="none"
      onRequestClose={onDismiss}
    >
      <Pressable style={menuStyles.modalOverlay} onPress={onDismiss}>
        <Animated.View
          style={[
            menuStyles.menuContainer,
            {
              transform: [{ translateY: slideAnim }],
            },
          ]}
        >
          <Pressable onPress={(e) => e.stopPropagation()}>
            <View>
              {sections.map((section, sectionIndex) => (
                <View key={sectionIndex}>
                  {section.label && (
                    <Text
                      variant="labelSmall"
                      style={menuStyles.menuSectionLabel}
                    >
                      {section.label}
                    </Text>
                  )}
                  {section.items.map((item, itemIndex) => {
                    if (item.render) {
                      return <View key={itemIndex}>{item.render()}</View>
                    }

                    return (
                      <Pressable
                        key={itemIndex}
                        style={[
                          menuStyles.menuItem,
                          item.disabled && { opacity: 0.5 },
                        ]}
                        onPress={() =>
                          !item.disabled && handleItemPress(item.onPress)
                        }
                        disabled={item.disabled}
                      >
                        {typeof item.icon === 'string' ? (
                          <Icon source={item.icon} size={24} />
                        ) : (
                          item.icon
                        )}
                        <Text style={menuStyles.menuItemText}>
                          {item.title}
                        </Text>
                      </Pressable>
                    )
                  })}
                  {sectionIndex < sections.length - 1 && <Divider />}
                </View>
              ))}
            </View>
          </Pressable>
        </Animated.View>
      </Pressable>
    </Modal>
  )
}
